import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Table, Button } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex } from 'atoms';
import { buildTaxSummary, formatRate } from '../taxSummary';
import InvoiceSlabTable from 'componentsv2/common/InvoiceSlabTable';

const Sheet = styled(Box)`
	background: #fff;
	padding: 8px 4px 16px;
	color: #1f2933;
`;

const Title = styled.div`
	font-size: 24px;
	font-weight: 800;
	color: #1e293b;
`;

const SellerName = styled.div`
	color: #1e3a5f;
	font-weight: 700;
	font-size: 16px;
	margin: 18px 0 8px;
`;

const Label = styled.div`
	font-size: 13px;
	font-weight: 700;
	color: #1f2933;
	margin-bottom: 4px;
`;

const Muted = styled.div`
	color: #64748b;
	font-size: 12px;
	line-height: 1.6;
	/* Wrap long addresses (seller / Bill To / Ship To) onto multiple lines. */
	white-space: normal;
	overflow-wrap: anywhere;
	word-break: break-word;
`;

const Divider = styled.div`
	border-top: 1px solid #e5e7eb;
	margin: 18px 0;
`;

const TotalsRow = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 6px 12px;
	font-size: 13px;
	color: #475569;
`;

const GrandTotalRow = styled(TotalsRow)`
	background: #f3f4f6;
	color: #15233a;
	font-weight: 700;
	font-size: 15px;
	border-radius: 4px;
`;

const Logo = styled.img`
	height: 65px;
	display: block;
`;

const TermsTitle = styled.div`
	font-size: 15px;
	font-weight: 700;
	color: #1f2933;
	margin: 4px 0 8px;
`;

const formatINR = (value) => `₹ ${(Number(value) || 0).toLocaleString('en-IN')}`;
const partyIdRows = (gstin, pan) =>
	[gstin && `GST: ${gstin}`, pan && `PAN Number: ${pan}`].filter(Boolean);

// Warehousing projects only — the preview's computed figures, carrying the slab walk.
const warehouseContextOf = (context) =>
	context?.isWarehouseFlow ? context?.preview?.warehouse_context || null : null;

const resolveTaxMode = (payload, issuerGstin, clientGstin) => {
	if (payload?.resolvedTaxMode) return payload.resolvedTaxMode;
	if (payload?.taxMode) return payload.taxMode;
	if (!issuerGstin || !clientGstin) return 'IGST';
	return String(issuerGstin).slice(0, 2) === String(clientGstin).slice(0, 2)
		? 'CGST_SGST'
		: 'IGST';
};
function InvoiceReviewModal({
	visible,
	context,
	payload,
	loading,
	onBack,
	onConfirm,
	confirmLabel,
}) {
	const meta = payload?.meta || {};
	const issuersList = context?.issuers || [];
	const issuerRaw =
		issuersList.find((i) => (i.value ?? i.id) === meta.bill_from) || issuersList[0] || {};
	const lineItems = useMemo(() => payload?.lineItems || [], [payload]);
	const totals = useMemo(
		() => payload?.totals || { subtotal: 0, taxTotal: 0, total: 0 },
		[payload]
	);

	const clientGstin = payload?.clientGstin || context?.clientGstin || '';
	const clientPan = context?.clientPan || '';
	const taxMode = resolveTaxMode(payload, issuerRaw.gstin, clientGstin);

	const lineColumns = [
		{ title: 'Item', dataIndex: 'item', width: 140, render: (t) => t || '-' },
		{
			title: 'Description',
			dataIndex: 'description',
			render: (t) => (
				<div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{t || '-'}</div>
			),
		},
		{ title: 'SAC Code', dataIndex: 'sac_code', width: 90, render: (t) => t || '-' },
		{ title: 'QTY', dataIndex: 'qty', width: 55, render: (t) => t ?? '-' },
		{ title: 'Rate', dataIndex: 'rate', width: 90, render: formatINR },
		{ title: 'Amount', dataIndex: 'amount', width: 90, render: formatINR },
		{ title: 'Tax Amount', dataIndex: 'taxAmount', width: 100, render: formatINR },
		{ title: 'Gross Amount', dataIndex: 'grossAmount', width: 110, render: formatINR },
	];

	const taxRows = useMemo(() => buildTaxSummary(lineItems, taxMode), [lineItems, taxMode]);

	const warehouseCtx = warehouseContextOf(context);

	const taxColumns = [
		{ title: 'Tax Type', dataIndex: 'tax_type' },
		{ title: 'Tax Code', dataIndex: 'tax_code' },
		{ title: 'Tax Basis', dataIndex: 'basis', render: formatINR },
		{ title: 'Tax Rate', dataIndex: 'rate', render: formatRate },
		{ title: 'Tax Amount', dataIndex: 'amount', render: formatINR },
	];

	return (
		<Modal
			title="Review Invoice Preview"
			open={visible}
			onCancel={onBack}
			width={1000}
			centered
			styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
			footer={[
				<Button key="back" onClick={onBack}>
					← Back
				</Button>,
				<Button key="confirm" type="primary" loading={loading} onClick={onConfirm}>
					Confirm &amp; {confirmLabel} →
				</Button>,
			]}
		>
			<Sheet>
				<Flex justifyContent="space-between" alignItems="flex-start">
					<Logo
						src="https://ripplr-public-asset.s3.ap-south-1.amazonaws.com/ripplr-log.png"
						alt="RIPPLR"
					/>
					<Box style={{ textAlign: 'right' }}>
						<Title>Tax Invoice</Title>
						<Muted style={{ marginTop: 6 }}>
							<div>
								<b>Invoice:</b> Auto (on generate)
							</div>
							<div>
								<b>Invoice Date:</b> {moment().format('DD/MM/YYYY')}
							</div>
						</Muted>
					</Box>
				</Flex>

				<SellerName>{issuerRaw.legal_name || '—'}</SellerName>
				<Box style={{ maxWidth: '62%' }}>
					<Muted>
						<div>{issuerRaw.address}</div>
						<div>PAN Number: {issuerRaw.pan || '—'}</div>
						<div>GSTIN: {issuerRaw.gstin || '—'}</div>
						{issuerRaw.website ? <div>website: {issuerRaw.website}</div> : null}
					</Muted>
				</Box>

				<Divider />

				<Flex style={{ gap: 40 }}>
					<Box flex="1">
						<Label>
							{clientGstin ? 'Bill To' : 'B2C'}
							{context?.client ? ` - ${context.client}` : ''}
						</Label>
						<Muted>
							<div>{payload?.billTo || '-'}</div>
							{partyIdRows(clientGstin, clientPan).map((line, i) => (
								<div key={line} style={i === 0 ? { marginTop: 6 } : undefined}>
									{line}
								</div>
							))}
						</Muted>
					</Box>
					<Box flex="1">
						<Label>Ship To</Label>
						<Muted>{payload?.shipTo || '-'}</Muted>
					</Box>
				</Flex>

				<Divider />

				<Flex
					style={{
						gap: 32,
						background: '#f8fafc',
						borderRadius: 6,
						padding: '12px 16px',
					}}
				>
					<Box flex="1">
						<Label>Due Date</Label>
						<Muted>
							{meta.due_date ? moment(meta.due_date).format('DD-MM-YYYY') : '-'}
						</Muted>
					</Box>
					<Box flex="1">
						<Label>PO No.</Label>
						<Muted>{meta.po_number || '-'}</Muted>
					</Box>
					<Box flex="1">
						<Label>Shipping Method</Label>
						<Muted>{(context?.shippingMethods || []).join(', ') || '-'}</Muted>
					</Box>
					<Box flex="1">
						<Label>WO No.</Label>
						<Muted>{meta.wo_number || '-'}</Muted>
					</Box>
				</Flex>

				<Box mt={3}>
					<Table
						rowKey="key"
						size="small"
						tableLayout="fixed"
						columns={lineColumns}
						dataSource={lineItems}
						pagination={false}
					/>
				</Box>

				<TotalsRow style={{ justifyContent: 'flex-end', marginTop: 8 }}>
					<b style={{ marginRight: 16 }}>Total Amount</b>
					<b>{formatINR(totals.subtotal)}</b>
				</TotalsRow>

				<Box mt={3} style={{ maxWidth: 520 }}>
					<Table
						rowKey="key"
						size="small"
						columns={taxColumns}
						dataSource={taxRows}
						pagination={false}
						// eslint-disable-next-line react/no-unstable-nested-components
						summary={() => (
							<Table.Summary.Row>
								<Table.Summary.Cell index={0} colSpan={4}>
									<b>Total</b>
								</Table.Summary.Cell>
								<Table.Summary.Cell index={1}>
									<b>{formatINR(totals.taxTotal)}</b>
								</Table.Summary.Cell>
							</Table.Summary.Row>
						)}
					/>
				</Box>

				{/* Slab-wise shipment revenue — same table the generation step shows, so
				    what is confirmed here matches what was reviewed there. */}
				{warehouseCtx && (
					<Box mt={3} style={{ maxWidth: 520 }}>
						<InvoiceSlabTable warehouseContext={warehouseCtx} />
					</Box>
				)}

				<Box style={{ width: 280, marginLeft: 'auto', marginTop: 16 }}>
					<TotalsRow>
						<span>Subtotal</span>
						<span>{formatINR(totals.subtotal)}</span>
					</TotalsRow>
					<TotalsRow>
						<span>Tax</span>
						<span>{formatINR(totals.taxTotal)}</span>
					</TotalsRow>
					<GrandTotalRow>
						<span>Total</span>
						<span>{formatINR(totals.total)}</span>
					</GrandTotalRow>
				</Box>

				<Divider />

				{(issuerRaw.terms || []).length ? (
					<>
						<TermsTitle>Terms &amp; Conditions</TermsTitle>
						{issuerRaw.terms.map((t) => (
							<Muted key={t} style={{ marginBottom: 4, paddingLeft: 8 }}>
								{t}
							</Muted>
						))}
					</>
				) : null}

				{(meta.bank || meta.account_number || meta.ifsc || meta.account_type) && (
					<Muted style={{ marginTop: 12 }}>
						<div style={{ fontWeight: 600, color: '#1f2933' }}>
							{issuerRaw.legal_name}
						</div>
						<div>Bank Name: {meta.bank || '-'}</div>
						<div>Bank Account Number: {meta.account_number || '-'}</div>
						<div>IFSC Code: {meta.ifsc || '-'}</div>
						<div>Account Type: {meta.account_type || '-'}</div>
					</Muted>
				)}

				<Divider />
				<Flex justifyContent="center">
					<Muted>
						If you have any questions about this invoice, please contact{' '}
						{issuerRaw.contact_email || 'accounts@ripplr.in'}
					</Muted>
				</Flex>
			</Sheet>
		</Modal>
	);
}

InvoiceReviewModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	context: PropTypes.object,
	payload: PropTypes.object,
	loading: PropTypes.bool,
	onBack: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	confirmLabel: PropTypes.string,
};

InvoiceReviewModal.defaultProps = {
	context: {},
	payload: {},
	loading: false,
	confirmLabel: 'Generate',
};

export default InvoiceReviewModal;
